import * as React from 'react'
import {type SyntheticEvent} from 'react'
import type {IconProp} from '@fortawesome/fontawesome-svg-core'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {exhaustive} from '@freckle/exhaustive'
import {addIfClassNames, addMaybeClassName} from '@freckle/student-materials/src/helpers/classnames'
import {type StringT, translateStr} from '../../helpers/string'
import Image from '../img'
import {caption1Style} from '../typography/text.module.scss'
import {
  btn,
  primary,
  secondary,
  tertiary,
  danger,
  warning,
  success,
  sm,
  md,
  lg,
  xl,
  selected,
  disabled,
  btnWithImage,
  inlineBtn,
  labelledBtn,
  label
} from './button-circular.module.scss'

export type StyleT = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'warning' | 'success'
export type SizeT = 'sm' | 'md' | 'lg' | 'xl'
export type FontAwesomeSizeT = 'xs' | 'sm' | 'lg' | '2x' | '3x' | '5x' | '7x' | '10x'
export type IconT =
  | {
      tag: 'icon'
      icon: IconProp
      spin?: boolean
    }
  | {
      tag: 'image'
      url: string
      addClass?: string
    }

type Props = {
  size?: SizeT
  icon: IconT
  iconSize?: FontAwesomeSizeT
  title: StringT
  style?: StyleT
  inline?: boolean
  isDisabled?: boolean
  isSelected?: boolean
  isToggle?: boolean
  // Shows the title as a visible label under the circle, inside the button. The label names the
  // button. When a button has visible text, use this rather than an `aria-label` (A11Y-110)
  showLabel?: boolean
  addClass?: string
  ariaCurrent?: boolean
  ariaExpanded?: boolean
  ariaControls?: string
  // can be directly applied to the DOM element
  autoFocus?: boolean
  // Omitted by default: a native <button> is already in the Tab order (A11Y-116)
  tabIndex?: number
  type?: 'button' | 'submit'
  'data-test'?: string
  // Names the button; defaults to the title unless `showLabel` is set
  'aria-label'?: string
  'aria-description'?: string
  id?: string | number
  // events
  onClick: (e: SyntheticEvent<Element, Event>) => void
  // Omitted by default: a native <button> already has the button role (A11Y-116)
  role?: string
}

export function ButtonCircular(props: Props): React.ReactElement {
  const [isSelectedState, setIsSelectedState] = React.useState<boolean>(false)

  const onBlur = () => {
    setIsSelectedState(false)
  }

  const onClick = (e: SyntheticEvent<Element, Event>) => {
    setIsSelectedState(isSelected => {
      return !isSelected
    })
    props.onClick(e)
  }

  const {
    style = 'primary',
    size = 'md',
    isDisabled = false,
    inline,
    addClass,
    icon,
    iconSize,
    title,
    isSelected,
    isToggle,
    showLabel = false,
    'aria-label': ariaLabel,
    onClick: _onClick,
    id,
    tabIndex,
    autoFocus = false,
    ariaCurrent = false,
    type = 'button',
    ariaExpanded,
    ariaControls,
    role,
    ...restProps
  } = props

  const styleClass = getStyleClass(style)
  const sizeClass = getSizeClass(size)
  const selectionState = isSelected ?? isSelectedState
  const className = isDisabled
    ? addMaybeClassName(`${btn} ${sizeClass} ${disabled}`, addClass)
    : addIfClassNames([
        [true, `${btn} ${styleClass} ${sizeClass}`],
        [icon.tag === 'image', btnWithImage],
        [selectionState, selected],
        [addClass !== null && addClass !== undefined, addClass],
        [inline, inlineBtn]
      ])

  const titleStr = translateStr(title)

  const mOnClick = isDisabled ? {} : {onClick}
  const mAriaCurrent = ariaCurrent ? {'aria-current': ariaCurrent} : {}
  const ariaPressedAttr =
    isToggle && (role === undefined || role === 'button') ? {'aria-pressed': isSelectedState} : {}
  const showAriaSelected =
    !isToggle && (isSelected !== null || isSelected !== undefined) && isSelectableRole(role)
  const ariaSelectedAttr = showAriaSelected ? {'aria-selected': isSelected} : {}

  const ariaCheckedAttr = role === 'checkbox' ? {'aria-checked': isSelected} : {}

  const addAriaExpanded = ariaExpanded !== null && ariaExpanded !== undefined
  const ariaExpandedAttr = addAriaExpanded ? {'aria-expanded': ariaExpanded} : {}

  const addAriaControls = ariaControls !== null && ariaControls !== undefined
  const ariaControlsAttr = addAriaControls ? {'aria-controls': ariaControls} : {}

  // The button carries the name, so the icon is always decorative (A11Y-115)
  const mAriaLabel = showLabel ? ariaLabel : (ariaLabel ?? titleStr)
  const iconNode = <Icon icon={icon} iconSize={iconSize} />

  return (
    <button
      className={showLabel ? labelledBtn : className}
      onBlur={onBlur}
      disabled={isDisabled}
      id={id ? id.toString() : undefined}
      tabIndex={tabIndex}
      autoFocus={autoFocus}
      type={type}
      role={role}
      aria-label={mAriaLabel}
      {...ariaCheckedAttr}
      {...mAriaCurrent}
      {...mOnClick}
      {...ariaPressedAttr}
      {...ariaSelectedAttr}
      {...ariaExpandedAttr}
      {...ariaControlsAttr}
      {...restProps}
    >
      {showLabel ? (
        <>
          <span className={className}>{iconNode}</span>
          <span className={addMaybeClassName(caption1Style, label)}>{titleStr}</span>
        </>
      ) : (
        iconNode
      )}
    </button>
  )
}

type IconProps = {
  icon: IconT
  iconSize?: FontAwesomeSizeT
}

function Icon({icon, iconSize}: IconProps): React.ReactElement<IconProps> {
  switch (icon.tag) {
    case 'icon':
      return <FontAwesomeIcon icon={icon.icon} spin={icon.spin} size={iconSize} />
    case 'image':
      return <Image src={icon.url} alt="" className={icon.addClass} />
    default:
      return exhaustive(icon, `IconT`)
  }
}

function getStyleClass(style: StyleT): string {
  switch (style) {
    case 'primary':
      return primary
    case 'secondary':
      return secondary
    case 'tertiary':
      return tertiary
    case 'danger':
      return danger
    case 'warning':
      return warning
    case 'success':
      return success
    default:
      return exhaustive(style, `StyleT`)
  }
}

function isSelectableRole(role?: string) {
  switch (role) {
    case 'gridcell':
    case 'option':
    case 'row':
    case 'tab':
      return true
    default:
      return false
  }
}

function getSizeClass(size: SizeT): string {
  switch (size) {
    case 'sm':
      return sm
    case 'md':
      return md
    case 'lg':
      return lg
    case 'xl':
      return xl
    default:
      return exhaustive(size, `SizeT`)
  }
}
